import { floatingToInstant, instantToFloating } from "@app/core";

import { pad } from "../../lib/format.js";

/** Format a Date as the value an <input type="datetime-local"> expects: YYYY-MM-DDTHH:MM (local). */
export function toLocalDateTime(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Convert a datetime-local string (no timezone) into an ISO UTC string. */
export function localDateTimeToIso(s: string): string {
  return new Date(s).toISOString();
}

/** Format a Date as <input type="date"> value: YYYY-MM-DD (local). */
export function toLocalDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The wall-clock parts (date + h/m) of a UTC instant, interpreted in `tz`
 * (null = device-local). Used to populate the form fields so an event anchored
 * to another zone shows its own local time, not the viewer's.
 */
export function wallClockOf(iso: string, tz: string | null): { dateStr: string; hour: number; minute: number } {
  if (!tz) {
    const d = new Date(iso);
    return { dateStr: toLocalDate(d), hour: d.getHours(), minute: d.getMinutes() };
  }
  const f = instantToFloating(iso, tz); // UTC fields hold the wall-clock in tz
  return {
    dateStr: `${f.getUTCFullYear()}-${pad(f.getUTCMonth() + 1)}-${pad(f.getUTCDate())}`,
    hour: f.getUTCHours(),
    minute: f.getUTCMinutes(),
  };
}

/**
 * Build a UTC instant from typed wall-clock parts interpreted in `tz`
 * (null = device-local, matching the legacy `new Date("YYYY-MM-DDTHH:MM")` path).
 */
export function wallClockToIso(dateStr: string, hour: number, minute: number, tz: string | null): string {
  if (!tz) return new Date(`${dateStr}T${pad(hour)}:${pad(minute)}:00`).toISOString();
  const [y, mo, d] = dateStr.split("-").map(Number);
  return floatingToInstant(new Date(Date.UTC(y!, mo! - 1, d!, hour, minute, 0, 0)), tz);
}

/**
 * End-of-day (23:59:59.999) of a date, interpreted in `tz` (null = device-local).
 * Used for the series-end (`until`) boundary so it lands at end-of-day in the
 * series' own zone, not the viewer's — otherwise editing a series anchored to a
 * different zone can drop/add the trailing occurrence.
 */
export function endOfDayIso(dateStr: string, tz: string | null): string {
  if (!tz) return new Date(`${dateStr}T23:59:59.999`).toISOString();
  const [y, mo, d] = dateStr.split("-").map(Number);
  return floatingToInstant(new Date(Date.UTC(y!, mo! - 1, d!, 23, 59, 59, 999)), tz);
}

/**
 * The deadline instant a live-edited form implies for urgency derivation:
 * `deadline_at` (the explicit deadline field) falling back to the scheduled
 * `due_at`, mirroring how {@link submitTaskForm} composes both. Events carry no
 * `deadline_at`, so they fall through to their scheduled date. null = neither.
 */
export function formDeadlineIso(v: {
  isEvent: boolean;
  deadlineStr: string;
  dateStr: string;
  hour: number | null;
  minute: number | null;
  allDay: boolean;
  tz: string | null;
}): string | null {
  if (!v.isEvent && v.deadlineStr) return new Date(`${v.deadlineStr}T00:00:00`).toISOString();
  if (!v.dateStr) return null;
  const h = v.allDay || v.hour === null ? 0 : v.hour;
  const m = v.allDay || v.minute === null ? 0 : v.minute;
  return wallClockToIso(v.dateStr, h, m, v.tz);
}

export interface QuickTime { label: string; hour: number; minute: number }

export const QUICK_TIMES: ReadonlyArray<QuickTime> = [
  { label: "8 AM", hour: 8, minute: 0 },
  { label: "9 AM", hour: 9, minute: 0 },
  { label: "Noon", hour: 12, minute: 0 },
  { label: "2 PM", hour: 14, minute: 0 },
  { label: "5 PM", hour: 17, minute: 0 },
  { label: "8 PM", hour: 20, minute: 0 },
];

/** Apply hour/minute to a date input value, returning the new datetime-local string. */
export function applyTime(dateStr: string, hour: number, minute: number): string {
  // dateStr can be either "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM"; we only care about the date half.
  const datePart = dateStr.slice(0, 10);
  return `${datePart}T${pad(hour)}:${pad(minute)}`;
}
